import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Construction, Clock, Mail, Eye, RotateCcw, ChevronDown, CheckCircle2, Bell, Smartphone, FileText, MessageSquare, CreditCard, BadgeCheck, UserPlus, Megaphone, CalendarClock, Briefcase, Upload } from "lucide-react";
import { Card, Empty, PageHeader, btn } from "@/components/common";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDemo } from "@/store/DemoStore";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export const COMM_TABS = [
  ["automation", "Automation"],
  ["templates", "Templates"],
  ["notifications", "Notifications"],
  ["surveys", "Post-Trip Surveys"],
  ["feedback", "Portal Feedback"],
];

export function CommunicationLayout() {
  const { pathname } = useLocation();
  const [unseen, setUnseen] = useState({ feedback: 0, surveys: 0 });

  useEffect(() => {
    let active = true;
    Promise.all([
      api.get("/feedback/unseen-count").then(({ data }) => data.count || 0).catch(() => 0),
      api.get("/surveys/unseen-count").then(({ data }) => data.count || 0).catch(() => 0),
    ]).then(([feedback, surveys]) => { if (active) setUnseen({ feedback, surveys }); });
    return () => { active = false; };
  }, [pathname]);

  return (
    <div className="max-w-5xl" data-testid="admin-communication-page">
      <PageHeader title="Communication" subtitle="Admin only · agency-wide communication." />
      <div className="mb-6 flex gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1 no-scrollbar" data-testid="communication-tabs">
        {COMM_TABS.map(([to, label]) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`communication-tab-${to}`}
            className={({ isActive }) => cn("flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3.5 py-1.5 text-sm font-medium transition-colors", isActive ? "bg-white text-navy-900 shadow-sm" : "text-slate-500 hover:text-navy-900")}
          >
            {label}
            {unseen[to] > 0 && <span data-testid={`communication-tab-dot-${to}`} className="h-2 w-2 shrink-0 rounded-full bg-orange-500" />}
          </NavLink>
        ))}
      </div>
      <Outlet />
    </div>
  );
}

export function CommunicationTab({ title, testId }) {
  return (
    <Card data-testid={testId}>
      <Empty icon={Construction} title={`${title} — coming soon`} text="This section is staged and ready to build." />
    </Card>
  );
}

/* ---------- Push Notifications catalogue ---------- */

const PUSH_NOTIFICATIONS = {
  client: [
    { icon: FileText, title: "New quote ready", trigger: "When an advisor prepares and sends a new quote.", body: "Your advisor prepared a new quote: 7-Night Mediterranean Cruise.", kind: "event" },
    { icon: FileText, title: "New document added", trigger: "When an advisor uploads a document to one of the traveler's trips.", body: 'Your advisor added "Final Itinerary.pdf" to 7-Night Mediterranean Cruise.', kind: "event" },
    { icon: MessageSquare, title: "New message from advisor", trigger: "When the advisor sends a message in MyTrip chat.", body: "New message from Jordan Avery", kind: "event" },
    { icon: CreditCard, title: "Payment reminder", trigger: "Automatically, when a final payment is approaching its due date.", body: "Final payment of $3,250 for 7-Night Mediterranean Cruise is due May 11, 2026.", kind: "scheduled" },
  ],
  agent: [
    { icon: MessageSquare, title: "New message from client", trigger: "When a traveler sends a message in MyTrip chat.", body: "New message from Jordan Avery", kind: "event" },
    { icon: UserPlus, title: "Client joined MyTrip", trigger: "When a traveler activates their MyTrip account.", body: "Jordan Avery activated their MyTrip account.", kind: "event" },
    { icon: BadgeCheck, title: "Quote approved", trigger: "When a traveler approves a quote and it becomes a booking.", body: "Jordan Avery approved TS-10428 — booking confirmed.", kind: "event" },
    { icon: CreditCard, title: "Payment authorized", trigger: "When a traveler authorizes a payment on an invoice.", body: "Jordan Avery authorized $3,250 on Final Payment for 7-Night Mediterranean Cruise.", kind: "event" },
    { icon: Upload, title: "New document uploaded", trigger: "When a traveler uploads a document to one of their trips.", body: 'Jordan Avery added "Passport.pdf" to 7-Night Mediterranean Cruise.', kind: "event" },
    { icon: CreditCard, title: "Card request", trigger: "When a traveler requests to add or remove a payment card.", body: "Jordan Avery requested to add a card (Visa ••4242).", kind: "event" },
    { icon: Briefcase, title: "New career application", trigger: "When someone applies through the public Careers page (admins).", body: "Taylor Rivera applied for Independent Travel Agent.", kind: "event" },
    { icon: Megaphone, title: "New announcement", trigger: "When an admin posts an agency-wide announcement (all advisors).", body: "Q3 Supplier Promotions Are Live", kind: "event" },
    { icon: CalendarClock, title: "Weekly departure digest", trigger: "Automatically, every Monday morning.", body: "Your Monday Countdown Digest is ready — pending quotes, payments due, and your numbers.", kind: "scheduled" },
  ],
};

function KindBadge({ kind }) {
  return kind === "scheduled"
    ? <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-600"><CalendarClock className="h-3 w-3" />Scheduled</span>
    : <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-600"><Bell className="h-3 w-3" />Real-time</span>;
}

function PushCard({ n }) {
  const Icon = n.icon;
  return (
    <Card className="p-4 sm:p-5" data-testid={`push-notification-${n.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
      <div className="flex items-start gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-navy-50 text-navy-700"><Icon className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-display text-[15px] font-semibold text-navy-900">{n.title}</span>
            <KindBadge kind={n.kind} />
          </div>
          <p className="mt-1 text-sm text-slate-500">{n.trigger}</p>
          <div className="mt-2.5 flex items-start gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
            <Smartphone className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
            <p className="text-xs leading-relaxed text-slate-600"><span className="font-semibold text-navy-800">{n.title}</span> — {n.body}</p>
          </div>
        </div>
      </div>
    </Card>
  );
}

function PushGroup({ title, subtitle, items }) {
  return (
    <div className="space-y-3">
      <div className="px-1">
        <h3 className="font-display text-base font-semibold text-navy-900">{title}</h3>
        <p className="text-sm text-slate-500">{subtitle}</p>
      </div>
      {items.map((n) => <PushCard key={n.title} n={n} />)}
    </div>
  );
}

export function NotificationsTab() {
  const total = PUSH_NOTIFICATIONS.client.length + PUSH_NOTIFICATIONS.agent.length;
  return (
    <div className="space-y-6" data-testid="communication-notifications-page">
      <Card className="flex flex-col gap-3 p-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 font-display text-base font-semibold text-navy-900"><Bell className="h-4 w-4 text-[#C9A96E]" />Web Push Notifications</h2>
          <p className="mt-1 text-sm text-slate-500">Every push notification configured in Compass. Each one is delivered to a user's device the moment its trigger fires — but only for users who have turned on push for that portal. These are live device notifications, not in-app messages or emails.</p>
        </div>
        <span className="shrink-0 self-start rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">{total} configured</span>
      </Card>
      <PushGroup title="Sent to travelers · MyTrip" subtitle="Delivered to clients who have enabled push in the MyTrip portal." items={PUSH_NOTIFICATIONS.client} />
      <PushGroup title="Sent to advisors · MyCompass" subtitle="Delivered to agents and admins who have enabled push in the MyCompass portal." items={PUSH_NOTIFICATIONS.agent} />
    </div>
  );
}

/* ---------- Automations ---------- */

const PLACEHOLDERS = [
  ["{client_name}", "Traveler's name"],
  ["{trip_title}", "Trip name"],
  ["{booking_number}", "Booking / quote #"],
  ["{departure_date}", "Departure date"],
  ["{final_due}", "Final payment amount"],
  ["{final_due_date}", "Final payment date"],
  ["{advisor_name}", "Assigned advisor"],
  ["{agency_name}", "Agency name"],
  ["{survey_link}", "Post-trip survey link"],
];

const SAMPLE = {
  "{client_name}": "Jordan Avery",
  "{trip_title}": "7-Night Mediterranean Cruise",
  "{booking_number}": "TS-10428",
  "{departure_date}": "May 14, 2026",
  "{final_due}": "$3,250",
  "{final_due_date}": "May 11, 2026",
  "{survey_link}": "https://app.gotopsail.com/survey/sample-link",
};

function fill(text, extra) {
  const map = { ...SAMPLE, ...extra };
  return (text || "").replace(/\{[a-z_]+\}/g, (m) => (m in map ? map[m] : m));
}

function AutomationCard({ auto, onSave, extra }) {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(auto.enabled);
  const [subject, setSubject] = useState(auto.subject);
  const [body, setBody] = useState(auto.body);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const focused = useRef("body");

  const dirty = enabled !== auto.enabled || subject !== auto.subject || body !== auto.body;

  const save = async (patch) => {
    setSaving(true);
    await onSave({ id: auto.id, ...patch });
    setSaving(false);
  };

  const reset = () => { setSubject(auto.subject); setBody(auto.body); setEnabled(auto.enabled); };

  const insert = (token) => {
    const target = focused.current === "subject" ? subjectRef.current : bodyRef.current;
    const setter = focused.current === "subject" ? setSubject : setBody;
    const val = focused.current === "subject" ? subject : body;
    const start = target?.selectionStart ?? val.length;
    const end = target?.selectionEnd ?? val.length;
    const next = val.slice(0, start) + token + val.slice(end);
    setter(next);
    requestAnimationFrame(() => {
      if (target) { target.focus(); const pos = start + token.length; target.setSelectionRange(pos, pos); }
    });
  };

  return (
    <Card className={cn("overflow-hidden transition-opacity", !enabled && "opacity-70")} data-testid={`automation-${auto.id}`}>
      <div className="flex items-center gap-3 p-4 sm:px-5">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          data-testid={`automation-header-${auto.id}`}
        >
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-navy-50 text-navy-700"><Mail className="h-4 w-4" /></span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              <span className="truncate font-display text-[15px] font-semibold text-navy-900">{auto.name}</span>
              {enabled ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600"><CheckCircle2 className="h-3 w-3" /> On</span>
              ) : (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-400">Off</span>
              )}
            </span>
            <span className="mt-0.5 flex items-center gap-1.5 text-[11px] font-medium text-slate-400">
              <Clock className="h-3 w-3" /> {auto.timingLabel}
            </span>
          </span>
          <ChevronDown className={cn("h-4 w-4 shrink-0 text-slate-400 transition-transform", open && "rotate-180")} />
        </button>
        <Switch checked={enabled} onCheckedChange={(v) => { setEnabled(v); save({ enabled: v }); }} data-testid={`automation-toggle-${auto.id}`} />
      </div>

      {open && (
        <div className="border-t border-slate-100 p-5 sm:px-5" data-testid={`automation-body-wrap-${auto.id}`}>
          <p className="mb-4 text-sm text-slate-500">{auto.description}</p>

          <div className="mb-4">
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Insert placeholder</p>
            <div className="flex flex-wrap gap-1.5">
              {PLACEHOLDERS.map(([token, desc]) => (
                <button
                  key={token}
                  type="button"
                  title={desc}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => insert(token)}
                  data-testid={`automation-chip-${auto.id}-${token.replace(/[{}]/g, "")}`}
                  className="rounded-md bg-slate-100 px-2 py-1 font-mono text-[11px] text-navy-700 transition-colors hover:bg-navy-900 hover:text-white"
                >
                  {token}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">Subject</label>
              <input
                ref={subjectRef}
                value={subject}
                onFocus={() => { focused.current = "subject"; }}
                onChange={(e) => setSubject(e.target.value)}
                data-testid={`automation-subject-${auto.id}`}
                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-navy-900 outline-none transition-colors focus:border-navy-500 focus:ring-2 focus:ring-navy-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">Message</label>
              <Textarea
                ref={bodyRef}
                value={body}
                onFocus={() => { focused.current = "body"; }}
                onChange={(e) => setBody(e.target.value)}
                rows={9}
                data-testid={`automation-body-${auto.id}`}
                className="resize-y rounded-xl border-slate-200 text-sm leading-relaxed text-navy-900 focus-visible:ring-navy-100"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
            <button onClick={() => setPreview(true)} className={btn.ghost} data-testid={`automation-preview-${auto.id}`}>
              <Eye className="h-4 w-4" /> Preview
            </button>
            <div className="flex items-center gap-2">
              {dirty && (
                <button onClick={reset} className={btn.ghost} data-testid={`automation-reset-${auto.id}`}>
                  <RotateCcw className="h-3.5 w-3.5" /> Discard
                </button>
              )}
              <button
                onClick={() => save({ subject, body, enabled })}
                disabled={!dirty || saving}
                className={btn.primary}
                data-testid={`automation-save-${auto.id}`}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <EmailPreview open={preview} onClose={() => setPreview(false)} auto={auto} subject={subject} body={body} extra={extra} />
    </Card>
  );
}

function EmailPreview({ open, onClose, auto, subject, body, extra }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl p-0 overflow-hidden" data-testid={`automation-preview-dialog-${auto.id}`}>
        <DialogHeader className="border-b border-slate-100 px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-base text-navy-900"><Eye className="h-4 w-4 text-navy-600" /> Preview · {auto.name}</DialogTitle>
          <p className="text-xs text-slate-400">Rendered with sample traveler data — nothing is sent.</p>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto p-6">
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="rounded-t-2xl bg-navy-900 px-5 py-4 text-white">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/60">{fill("{agency_name}", extra)}</p>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span className="font-semibold">Booking {fill("{booking_number}", extra)}</span>
                <span className="text-white/70">·</span>
                <span className="text-white/90">Departs {fill("{departure_date}", extra)}</span>
              </div>
            </div>
            <div className="px-5 py-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Subject</p>
              <p className="mt-1 text-sm font-semibold text-navy-900" data-testid={`preview-subject-${auto.id}`}>{fill(subject, extra)}</p>
              <hr className="my-4 border-slate-100" />
              <div className="whitespace-pre-line text-sm leading-relaxed text-slate-700" data-testid={`preview-body-${auto.id}`}>{fill(body, extra)}</div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AutomationTab() {
  const { automations = [], settings, updateAutomation } = useDemo();
  const extra = useMemo(() => ({
    "{advisor_name}": settings?.advisorName || "Your Advisor",
    "{agency_name}": settings?.agencyName || "Topsail Travel",
  }), [settings]);

  return (
    <div className="space-y-4" data-testid="communication-automation-page">
      <Card className="flex flex-col gap-3 p-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="font-display text-base font-semibold text-navy-900">Email Automations</h2>
          <p className="mt-1 text-sm text-slate-500">Toggle each automation on or off and tap a card to personalize the wording. Timing is fixed to the agency schedule. Every email automatically shows the booking number and departure date at the top so travelers always know which trip it refers to.</p>
        </div>
      </Card>

      {automations.length === 0 ? (
        <Card><Empty icon={Mail} title="No automations" text="Automation settings will appear here." /></Card>
      ) : (
        automations.map((a) => <AutomationCard key={a.id} auto={a} onSave={updateAutomation} extra={extra} />)
      )}
    </div>
  );
}
